import React, { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { AuthForm } from '../../../../components/AuthForm/AuthForm'
import { LoadingForm } from '../../../../components/LoadingForm/LoadingForm'
import { Title } from '../../../../components/Title/Title'
import { VerifyEmail } from '../../../../components/VerifyEmail/VerifyEmail'
import { ROUTES } from '../../../../constants/routes'
import { useAuth } from '../../../../hooks/useAuth'
import { useRouter } from '../../../../hooks/useRouter'
import translations from '../../../../translations/en.json'
import { LoginForm } from './components/LoginForm'
import { useVerifyEmail } from './hooks/useVerifyEmail'

export const LoginPage = (): React.ReactElement => {
  const { user, isLoading, handleLogin } = useAuth()
  const { location, ...router } = useRouter()
  const [error, setError] = useState<string | undefined>()
  const { handleVerifyCode, handleResendVerifyEmail } = useVerifyEmail({ user })

  let goto = ROUTES.DASHBOARD
  if (router.query.goto) {
    const route = decodeURIComponent(
      Array.isArray(router.query.goto) ? router.query.goto[0] : router.query.goto,
    )
    if (location.pathname !== route) {
      goto = route
    } else {
      goto = ROUTES.DASHBOARD
    }
  } else if (window.location.search.includes('code')) {
    goto = ROUTES.ACCEPT_INVITE + window.location.search
  }

  if (isLoading) {
    return (
      <AuthForm
        useProgress={false}
        error={error}
        cardFooter={
          <>
            <span className="d-block w-100 text-center">
              <Link
                to={{ pathname: ROUTES.SIGNUP, search: location.search }}
                className="ms-2 text-body-tertiary"
              >
                Need an account? Sign up
              </Link>
            </span>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={process.env.REACT_APP_REGISTER_URL}
              className="d-block w-100 mt-3 text-center text-body-tertiary"
            >
              Not looking to start your own program? Click here to register for one
            </a>
          </>
        }
      >
        <Title value="Loading..." />
        <LoadingForm />
      </AuthForm>
    )
  }

  if (!user) {
    return (
      <>
        <Title value="Login" />
        <AuthForm
          useProgress={false}
          error={error}
          cardFooter={
            <>
              <span className="d-block w-100 text-center">
                <Link
                  to={{ pathname: ROUTES.SIGNUP, search: location.search }}
                  className="ms-2 text-body-tertiary"
                >
                  Need an account? Sign up
                </Link>
              </span>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={process.env.REACT_APP_REGISTER_URL}
                className="d-block w-100 mt-3 text-center text-body-tertiary"
              >
                Not looking to start your own program? Click here to register for one
              </a>
            </>
          }
        >
          <LoginForm
            onSubmit={async (values) => {
              try {
                await handleLogin(values.username, values.password)
                router.push(goto)
                setError(undefined)
              } catch (e: any) {
                setError(
                  e.message ||
                    `${(translations as any)['error.default_toast_header']}. ${
                      (translations as any)['error.default_toast_message']
                    }`,
                )
              }
            }}
          />
        </AuthForm>
      </>
    )
  }

  const isEmailVerified = !!user?.is_email_verified
  if (isEmailVerified) {
    return <Navigate to={goto} replace />
  }

  return (
    <>
      <Title value="Verify email" />
      <AuthForm
        useProgress={false}
        error={error}
        cardFooter={
          <>
            {!user && (
              <span className="d-block w-100 text-center">
                <Link
                  to={{ pathname: ROUTES.SIGNUP, search: location.search }}
                  className="ms-2 text-body-tertiary"
                >
                  Need an account? Sign up
                </Link>
              </span>
            )}
            {!!user && (
              <span className="d-block w-100 text-center">
                <Link
                  to={{ pathname: ROUTES.LOGOUT, search: location.search }}
                  className="ms-2 text-body-tertiary"
                >
                  Not {user.first_name}? Logout
                </Link>
              </span>
            )}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={process.env.REACT_APP_REGISTER_URL}
              className="d-block w-100 mt-3 text-center text-body-tertiary"
            >
              Not looking to start your own program? Click here to register for one
            </a>
          </>
        }
      >
        <VerifyEmail
          has_error={!!error}
          onResend={async () => {
            try {
              await handleResendVerifyEmail()
            } catch (e: any) {
              const message = e.message || (translations as any)['error.default_toast_message']
              setError(message)
            }
          }}
          onComplete={async (code: string) => {
            try {
              await handleVerifyCode(code)
            } catch (e: any) {
              const message = e.message || (translations as any)['error.default_toast_message']
              setError(message)
            }
          }}
        />
      </AuthForm>
    </>
  )
}
